import { Accessor, BorderX, ColorX, GFunc, LocaleX, ZArray, ZMath } from "IZOX/STATIC";
import { HStack, Spacer, VStack, XButton, XDiv, XImage, XInput, XScroll, XText } from "IZOX/_X";
import _ from "lodash";
// eslint-disable-next-line no-unused-vars
import React, { CSSProperties, useCallback, useEffect, useRef, useState } from "react";
import imgUploadDrag from "../_images/uploaddrag.png";
import imgFile from "../_images/file.png";
import imgPicture from "../_images/picture.png";
import imgUpload from "../_images/upload.png";
import imgDelete from "../_images/delete.png";
import AtthExpress from "IZOX/ARCS/API/AtthExpress";
import { DOMAIN, SITEBASE } from "__SYSDefault/Domain";
import ReqConfig from "__SYSDefault/_ReqConfig";

/**
 * @typedef {import("IZOX/_X/XInput").xinputparams} xinputparams
 * @typedef {import("IZOX/_X/XInput").xinputstyleparams} xinputstyleparams
 * @typedef {import("IZOX/_X/XDiv").xdivparams} xdivparams
 * @typedef {import("IZOX/STATIC/ZValidate").fvalidate} fvalidate
 * @typedef {import("IZOX/__Typedef/common").lang} lang
 * 
 * @typedef {Object} qfdragdropparam
 * @property {String} name
 * @property {lang} label
 * @property {any} defaultValue
 * @property {Function} _onChange
 * @property {Function} _onSetDefault
 * @property {any} formValue
 * @property {String} color
 * @property {String} bColor
 * @property {String} btnColor
 * @property {String} btnBgColor
 * @property {String} previewColor
 * @property {Boolean} preview
 * @property {Boolean} instantUpload
 * @property {Boolean} multiple
 * @property {Boolean} dropzone
 * @property {Boolean} button
 * @property {({ref, readOnly, dropzone, button, onDragOver, onDragLeave, onDrop, onBtnClick, dragIn, color, bColor, btnColor, btnBgColor}) => JSX.Element} renderUpload
 * @property {({value, preview, readOnly, color, bColor, onImageClick, onUploadClick, onDeleteClick}) => JSX.Element} renderPreview
 * @property {Boolean} readOnly
 * @property {"V" | "H"} stackDirection
 * @property {String} remarks
 * @property {Number} fileSizeLimit
 * @property {xdivparams} uploadZoneProps
 * @property {xdivparams} uploadZoneInnerProps
 * @property {xdivparams} previewZoneProps
 * @property {Boolean} noFileCaption
 * @property {Boolean} canDelete
 * @property {Boolean} keepDeleted
 * 
 * @typedef {Object} fileItem
 * @property {String} originalName
 * @property {Number} size
 * @property {import("IZOX/STATIC/MimeTypeX").mimeType} type
 * @property {String} refID
 * @property {Number} complete
 * @property {File} oriFile 
 * 
 * @typedef {qfdragdropparam & xinputparams & xinputstyleparams} qfdropuploadparams
 * 
 * @param {qfdropuploadparams} param0
 * @returns 
 */
function QFDropUpload({
  name = undefined,
  label = undefined,
  _onChange = null,
  _onSetDefault = null,
  defaultValue = undefined,
  formValue = {},
  color = ColorX.CSS("blue"),
  bColor = ColorX.CSS("lightGrey"),
  btnColor = ColorX.CSS("pureWhite"),
  btnBgColor = ColorX.CSS("blue"),
  previewColor = ColorX.CSS("blue"),
  preview = true,
  instantUpload = false,
  multiple = false,
  dropzone = true,
  button = true,
  renderUpload = undefined,
  renderPreview = undefined,
  readOnly = false,
  stackDirection = "V",
  remarks = "",
  accept = undefined,
  fileSizeLimit = 150,
  uploadZoneProps = {},
  uploadZoneInnerProps = {},
  previewZoneProps = {},
  noFileCaption = false,
  canDelete = true,
  keepDeleted = true,
  updateVia = undefined, 
  updateField = undefined,
  forceUpdateField = undefined,
  performanceBoost = undefined,
  ...others
}){

  const ref = useRef(null);
  const inputRef = useRef(null);
  const [value, setValue] = useState(defaultValue ?? []);
  const [dragIn, setDragIn] = useState(false);
  const [, updateState] = React.useState();
  const forceUpdate = React.useCallback(() => updateState({}), []);

  const GetName = useCallback(() => {
    if(_.isUndefined(name)) return name;
    return instantUpload ? name : `${name}.${ReqConfig.AttachmentFieldKey}`;
  }, [name, instantUpload]);

  useEffect(() => {
    if(defaultValue) {
      _onSetDefault?.(GetName(), defaultValue);
      setValue(defaultValue);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [defaultValue]);

  useEffect(() => {
    let _value = Accessor.Get(formValue, GetName());
    setValue(_value);
  }, [formValue, GetName]);

  useEffect(() => {
    if(!dropzone) return;

    const dragOver = (e) => {
      e.preventDefault();
    };
    
    const dragLeave = (e) => {
      e.preventDefault();
    };

    document.addEventListener("dragover", dragOver);
    document.addEventListener("dragleave", dragLeave);

    return () => {
      document.removeEventListener("dragover", dragOver);
      document.removeEventListener("dragleave", dragLeave);
    };

  }, [dropzone]);

  if(_.isUndefined(GetName())) return null;

  const onUploadProgress = (e, idx) => {
    let {loaded, total} = e;
    let newValue = value ?? [];
    newValue[idx] = {
      ...value?.[idx], 
      complete: Math.round(loaded / total * 10000) /100
    };
    _onChange?.(GetName(), newValue);
    forceUpdate();
  };

  const uploadToBlob = async (curIdx, files) => {
    let newValue = value ?? [];
    for(let i=0; i<files.length; i++){
      const o = files[i];
      let res = await AtthExpress.Upload(o, (e) => onUploadProgress(e, i), remarks);
      let {Success, payload} = res;
      if(Success){
        if(payload){
          newValue[curIdx + i] = {
            ...value?.[curIdx+ i], 
            ...payload[0], 
            complete: 100, 
            done: true
          };
        }
      }
    }
    _onChange?.(GetName(), newValue);
    forceUpdate();
  };

  const onFileUpload = async (files) => {
    console.log(multiple, value);
    if(!files) return;
    if(!multiple) files = [files[0]];
    if(!multiple && value?.length >= 1){
      await DeleteIdx(0);
    };

    //check size limit
    let _limit = fileSizeLimit * 1000 * 1000;
    if(_.some(files, o => o.size > _limit)){
      GFunc.Pop(
        {
          EN: "File size too big",
          TC: "檔案太大",
        }, 
        {
          EN: "File size exceeds: " + ZMath.FileSizeUnit(_limit).join(" "),
          TC: "檔案大於限制: " + ZMath.FileSizeUnit(_limit).join(" "),
        }
      );
      return;
    }
    
    let fileInfo = _.map(files, o => ({oriFile: o, originalName: o.name, size: o.size, type: o.type}));
    let curIdx = value?.length ?? 0;
    let newValue = value ? [...value, ...fileInfo] : [...fileInfo];
    _onChange?.(GetName(), newValue);
    forceUpdate();
    
    if(instantUpload){
      await uploadToBlob(curIdx, files);
    }
  };

  const acceptFiles = (file) => {
    let {type, name} = file;
    let extension = "." + name.split(".").pop();
    let _accept;
    if(_.isString(accept)){
      _accept = accept.split(",");
    }else{
      _accept = accept;
    }
    _accept = ZArray.Ensure(_accept);
    return _.some(_accept, (o, i) => {
      if(o.includes("*")){
        return type.startsWith(o.replace("*", ""));
      }if(o.startsWith(".")){
        return o === extension;
      }else{
        return type === o;
      }
    });
  };

  const onFileInput = (e) => {
    e.preventDefault();
    onFileUpload?.(e.target.files);
  };

  const onDragOver = (e) => {
    setDragIn(true);
  };

  const onDragLeave = (e) => {
    setDragIn(false);
  };

  const onDrop = (e) => {
    e.preventDefault();
    setDragIn(false);
    let _files = e.dataTransfer.files;
    if(!_.isEmpty(accept)){
      _files = _.filter(e.dataTransfer.files, o => acceptFiles(o));
    }
    if(_.isEmpty(_files)) return;
    onFileUpload?.(_files);
  };

  const onBtnClick = () => {
    inputRef.current?.click?.();
  };

  /**
   * 
   * @param {fileItem} file 
   * @param {Number} idx 
   */
  const onImageClick = async (file, idx) => {
    let {refID, complete, oriFile, type, blob} = file;

    if(blob) refID = blob;

    if(!_.isEmpty(refID) && !refID.startsWith("izox::")){
      if(refID.startsWith("http")){
        GFunc.ViewImage(`${refID}`);
      }else{
        GFunc.ViewImage(`${SITEBASE}/${refID}`);
      }
      return;
    }

    if(type?.startsWith("image/")){
      if(refID || complete >= 100){
        let res = await AtthExpress.GetBlobUrl(refID);
        let {url} = res;
        if(!url?.startsWith("http")){
          url = `${DOMAIN}${url?.slice(1)}`;
        }
        GFunc.ViewImage(url);
      }else{
        if(oriFile){
          let url = URL.createObjectURL(oriFile);
          GFunc.ViewImage(url);
        }
      }
      return;
    }

    if(refID || complete >= 100){
      let res = await AtthExpress.GetBlobUrl(refID);
      window.open(res.url);
    }else{
      if(oriFile){
        let url = URL.createObjectURL(oriFile);
        window.open(url);
      }
    }    
    
  };

  /**
   * 
   * @param {fileIt9em} file 
   * @param {Number} idx 
   */
  const onUploadClick = async (file, idx) => {

  };

  /**
   * 
   * @param {Number} idx 
   */
  const DeleteIdx = async (idx) => {
    let file = value?.[idx];
    let {refID} = file;
    if(instantUpload && !keepDeleted){
      await AtthExpress.Delete(refID);
    }

    let newValue = ZArray.DeleteIdx(value, idx);
    _onChange?.(GetName(), newValue);
    forceUpdate();
  };

  /**
   * 
   * @param {fileItem} file 
   * @param {Number} idx 
   */
  const onDeleteClick = async (file, idx) => {
    if(!canDelete) return;
    let {refID} = file;
    if(instantUpload && !keepDeleted){
      await AtthExpress.Delete(refID);
    }

    let newValue = ZArray.DeleteIdx(value, idx);
    _onChange?.(GetName(), newValue);
    forceUpdate();
  };

  function _renderUpload({ref, readOnly, dropzone, button, onDragOver, onDragLeave, onDrop, onBtnClick, dragIn, color, bColor, btnColor, btnBgColor}){
    if(readOnly) return;
    return (
      <VStack width={460} {...uploadZoneProps}>
        <VStack forwardRef={ref} fullWidth height={"auto"} position="relative"
          onDragOver={dropzone ? onDragOver : undefined} onDragLeave={dropzone ? onDragLeave : undefined}
          style={{
            backgroundImage: dragIn ?
            BorderX.Render({color: bColor, dashArray: [1]})
              : BorderX.Render({color: bColor, dashArray: [18, 12]})
          }} spacing={24} {...uploadZoneInnerProps}>
          <VStack pT={16} spacing={8} fullWidth onDrop={dropzone ? onDrop : undefined}>
            {renderDropZone({dropzone, color, dragIn})}
            {renderORLine({dropzone, button, bColor})}
            {renderButton({btnColor, btnBgColor, onBtnClick})}
          </VStack>
        </VStack>
      </VStack>
    );
  }

  function renderDropZone({dropzone, color, dragIn}){
    if(!dropzone) return;
    return (
      <VStack>
        <XImage source={imgUploadDrag} width={30}/>
        <XText color={color}>
          {LocaleX.Parse(dragIn?
            {
              EN: "Relase to upload",
              TC: "放手上傳文件"
            }
            : 
            {
              EN: "Drag file to upload",
              TC: "拖曳文件上傳"
            })}
        </XText>
      </VStack>
    );
  }

  function renderORLine({dropzone, button, bColor}){
    if(!dropzone || !button) return;
    return (
      <HStack spacing={8} width={"50%"} minWidth={200}>
        <XDiv bW={1} bColor={bColor}/>
        <XText color={bColor}>
        {LocaleX.Parse({
            EN: "OR",
            TC: "或"
          })}
        </XText>
        <XDiv bW={1} bColor={bColor}/>
      </HStack>
    );
  }

  function renderButton({btnColor, btnBgColor, onBtnClick}){
    if(!button) return;
    return (
      <XButton color={btnColor} bgColor={btnBgColor} 
        onPress={onBtnClick}
        round={50} pV={8} pH={24} width={200}>
        <HStack>
          <XText>
            {LocaleX.Parse({
              EN: "Choose File",
              TC: "選擇文件"
            })}
          </XText>
        </HStack>
      </XButton>
    );
  }

  function _renderPreview({value, preview, readOnly, color, bColor, onImageClick, onUploadClick, onDeleteClick}){
    if(!preview || _.isEmpty(value)) return;
    return (
      <VStack width={460} {...previewZoneProps}>
        {!noFileCaption && <HStack>
          <XText bold fullWidth textAlign="[" color={previewColor}>
            {LocaleX.Parse({
              EN: `File (${value?.length})`,
              TC: `檔案 (${value?.length})`
            })}
          </XText>
        </HStack>}
        <XScroll spacing={8} maxHeight={100} fullWidth>
          {_.map(value, (o, i) => {
            return renderFile({file: o, idx: i, readOnly, color, bColor, onImageClick, onUploadClick, onDeleteClick});
          })}
        </XScroll>
      </VStack>
    );
  }

  /**
   * 
   * @param {fileItem} file 
   * @param {Number} idx 
   */
  function renderFile({file, idx, readOnly, color, bColor, onImageClick, onUploadClick, onDeleteClick}){
    if(!file) return;
    let {originalName, size, complete, refID} = file;
    let isCompleted = refID || complete >= 100;
    let [temp, unit] = ZMath.FileSizeUnit(size);
    temp = _.isNaN(temp) ? 0 : temp;

    return (
      <HStack key={idx} bgColor={readOnly && idx%2 === 0 ? ColorX.CSS("aliceblue") : undefined}>
        <HStack spacing={8} HAlign="[" width={240}>
          <XImage source={imgFile} width={20} height={20} noResize/>
          <VStack HAlign="[" flexGrow={1} fullWidth>
            <XText color={color} fullWidth fontSize={12} 
              noWrap ellipsisMode="ellipsis">
              {isCompleted && originalName ? originalName : LocaleX.Parse({EN: "Uploading...", TC: "上載中..."})}
            </XText>
            <HStack width={"auto"} spacing={5}>
              <XText color={bColor} fontSize={12}>
                {String(ZMath.Round(temp, 2))}
              </XText>
              <XText color={bColor} fontSize={12}>
                {unit}
              </XText>
            </HStack>
          </VStack>
        </HStack>
        <Spacer/>
        {!readOnly && instantUpload
          && <HStack width={80}>
            <XText color={color} textAlign="]" fontSize={14}>
              {LocaleX.Parse({
                EN: isCompleted ? "Completed" : "Uploading",
                TC: isCompleted ? "已上傳" : "上傳中"
              })}
            </XText>
          </HStack>}
        <HStack HAlign="]" width={100}>
          <XButton padding={0} onPress={() => onImageClick(file, idx)} mR={8}
            tooltip={LocaleX.Parse({EN: "Preview", TC: "預覽"})}>
            <XImage source={imgPicture} width={20} p={4} noResize/>
          </XButton>
          {!isCompleted && instantUpload &&
            <XButton padding={0} onPress={() => onUploadClick(file, idx)} mR={8} 
              tooltip={LocaleX.Parse({EN: `${complete}%`, TC: `${complete}%`})}>
              <XImage source={imgUpload} width={20} p={4} noResize/>
            </XButton>}
          {(isCompleted || !instantUpload) && !readOnly && 
            <XButton padding={0} onPress={() => onDeleteClick(file, idx)} mR={8}  
              tooltip={LocaleX.Parse({EN: "Delete", TC: "刪除"})}>
              <XImage source={imgDelete} width={20} p={4} noResize/>
            </XButton>}
        </HStack>
      </HStack>
    );
  }


  switch(stackDirection){
    default: case "V":
      return (
        <VStack {...others}>
          <XInput type="file" style={{display: "none"}} forwardRef={inputRef}
                onChange={e => onFileInput?.(e)} accept={accept} multiple={multiple}/>
          {renderUpload?.({ref, readOnly, dropzone, button, onDragOver, onDragLeave, onDrop, onBtnClick, dragIn, color, bColor, btnColor, btnBgColor})
           ?? _renderUpload({ref, readOnly, dropzone, button, onDragOver, onDragLeave, onDrop, onBtnClick, dragIn, color, bColor, btnColor, btnBgColor})}
          {renderPreview?.({value, preview, readOnly, color, bColor, onImageClick, onUploadClick, onDeleteClick}) 
            ?? _renderPreview({value, preview, readOnly, color, bColor, onImageClick, onUploadClick, onDeleteClick})}
        </VStack>
      );
    case "H":
      return (
        <HStack {...others} HAlign="[" VAlign="^" spacing={10}>
          <XInput type="file" style={{display: "none"}} forwardRef={inputRef}
                onChange={e => onFileInput?.(e)} accept={accept} multiple={multiple}/>
          {renderUpload?.({ref, readOnly, dropzone, button, onDragOver, onDragLeave, onDrop, onBtnClick, dragIn, color, bColor, btnColor, btnBgColor})
           ?? _renderUpload({ref, readOnly, dropzone, button, onDragOver, onDragLeave, onDrop, onBtnClick, dragIn, color, bColor, btnColor, btnBgColor})}
          {renderPreview?.({value, preview, readOnly, color, bColor, onImageClick, onUploadClick, onDeleteClick}) 
            ?? _renderPreview({value, preview, readOnly, color, bColor, onImageClick, onUploadClick, onDeleteClick})}
        </HStack>
      );
  }

  

}

export default QFDropUpload;
